import { Suspense, useContext, useEffect, useMemo, useState } from 'react';
import { Route, Switch, Redirect } from 'react-router-dom';
import { connect, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import 'bootstrap/dist/css/bootstrap.min.css';
import routes, { NotFound, history, shouldHideNavbarForPath } from 'routes';
import { serviceRehydrateStart } from 'store/authentication/authenticationAction';
import { themeLocalStorage } from 'storage/theme';
import {
  isPseudo,
  changeLanguageInStorage,
  PSEUDO_LANGUAGE,
} from 'i18n-config';
import ErrorBoundaryResettable from 'components/Kizen/ErrorBoundaryResettable';
import Loader from 'components/Kizen/Loader';
import { snakeToCamelCaseKeys } from 'services/helpers';
import CatchError from 'components/CatchError/CatchError';
import AxiosErrorCatcher from 'components/CatchError/AxiosErrorCatcher';
import { Spacing } from './spacing';
import { Typing } from './typography';
import { ModalBackdrop } from './modalBackdrop';
import MonitorSessionTimeout from './monitorSessionTimeout';
import { useIframeCheck } from 'hooks/useIframeCheck';
import { PermissionsError } from 'services/errors';
import { TestingMode } from 'components/Layout/TestingMode';
import { RouteWithTitle } from 'hooks/useSetTitleOnLoad';
import { PageView } from './pageViewWrapper';
import DashboardAnalytics from './DashboardAnalytics';
import { maybeIdentifyUser } from 'utility/analytics';
import { useSkipAccessFetch } from 'hooks/useSkipAccessFetch';
import { NavBarProvider } from './navBarController';
import { useQuery } from '@tanstack/react-query';
import { PAGE_CONFIGS } from 'queries/query-keys';
import TeamMemberService from 'services/TeamMemberService';
import { maybeInjectPermanentItems } from 'pages/Account/pages/Toolbar/hooks/useRightColumn';
import CustomNavigation from 'components/CustomNavigation';
import CustomNavigationWrapper from 'components/CustomNavigation/Wrapper';
import { serializeToKey } from 'pages/Account/pages/Toolbar/utils';
import useToolbarAccessFiltering from 'pages/Account/pages/Toolbar/hooks/useToolbarAccessFiltering';
import { useLocation } from 'react-use';
import { StyledLoader } from './styles';
import { priorityPaths, isNonLandingPagePath } from 'routes/paths';
import { AUTHORIZED_EMBED_IFRAME_PATH } from 'services/AxiosService';
import '@fortawesome/fontawesome-svg-core/styles.css';
import { getRoute } from 'components/CustomNavigation/util';
import { DataManagerSessionProvider } from './dataManagerSession';
import '@kizen/kds/css';
import { ScrollbarInteractionManager } from '@kizen/kds/Scrollbar';
import { TranslationContextProvider } from '@kizen/kds/TranslationContext';
import { MonitorDeploymentVersion } from './MonitorDeployedVersion';
import { FloatingWindow } from 'ts-components/Plugins/FloatingWindow';
import { LogActivityContext } from 'ts-activities/LogActivityContext';
import { v4 } from 'uuid';
import { RouteScriptHandler } from 'ts-components/Plugins/RouteScriptHandler';
import { PaymentNeededHandler } from 'ts-components/PaymentNeeded';
import { InstrumentationProvider } from 'instrumentation/Provider';
import { BillingRouting } from './BillingRouting';
import { PluginContext } from 'ts-components/Plugins/context';
import { NotificationLinkHandler } from 'ts-components/NotificationLinkHandler';
import { AppBootstrapSuccess } from '@kizenapps/engine/react';
import { WYSIWYGCopyPaste } from 'components/WYSIWYG/copy-paste';
import { UI_THEMES } from '@kizen/kds/Colors';
import { applyTheme } from 'utility/theme';
import { getCurrentTeamMemberTheme } from 'store/authentication/selectors';

// Set the theme attribute synchronously, before the first paint, from the
// last known value in localStorage so we don't flash the light theme before
// the authenticated team member's actual theme loads.
applyTheme(
  Object.values(UI_THEMES).includes(themeLocalStorage.val)
    ? themeLocalStorage.val
    : UI_THEMES.LIGHT
);

const getUrlForItem = (item, toolbarItemsByFullId = {}) => {
  // Plugin toolbar items only know their real route once the live plugin item is
  // resolved from the plugin context, the same way CustomNavigation/Item.jsx does it
  const dynamicItem = toolbarItemsByFullId[item.id] ?? item;
  const route = getRoute(item, dynamicItem);

  if (route.path === '/') {
    return null;
  }

  if (item.isCustomObject) {
    return route.path;
  }

  // Scripts cannot be navigated to and should be skipped
  if (dynamicItem.script) {
    return null;
  }

  // External routes cannot be used as a landing page
  if (route.external) {
    return null;
  }

  if (item.isDefault) {
    return route.path;
  }

  return route.path;
};

export const getFirstInternalNavItem = (
  navItems,
  toolbarItemsByFullId = {}
) => {
  if (!navItems?.length) {
    return null;
  }

  for (const item of navItems) {
    // If there are children, we want the first eligible child (if any)
    if (item.children?.length) {
      for (const child of item.children) {
        const url = getUrlForItem(child, toolbarItemsByFullId);

        if (url) {
          return url;
        }
      }
    }

    // If there are no children, attempt to get the URL for the item itself
    const url = getUrlForItem(item, toolbarItemsByFullId);
    if (url) {
      return url;
    }
  }

  return null;
};

const ACCESS_ANY = 'any';
const urlsSections = {
  ads: 'adsSection',
  automations: 'automationsSection',
  broadcasts: 'broadcastsSection',
  clients: 'contactsSection',
  'custom-objects': 'customObjectsSection',
  forms: 'formsSection',
  'message-library': 'messageLibrarySection',
  'marketing-costs': 'marketingCostsSection',
  account: [
    { name: 'myProfileSection', checkKey: 'view' },
    { name: 'apiConnectionsSection', checkKey: ACCESS_ANY },
  ],
  orders: 'ordersSection',
  products: 'productsSection',
  settings: 'settingsSection',
  surveys: 'surveysSection',
};

const AppContexts = ({ children, enabled }) => {
  // This doesn't simply return children when enabled === false because that
  // caused the choose business page to select the business twice to.
  return <LogActivityContext enabled={enabled}>{children}</LogActivityContext>;
};

const App = ({ rehydrate, rehydrating }) => {
  const pluginApps = useSelector((s) => s.authentication.pluginApps);
  const access = useSelector((s) => s.authentication.access);
  const teamMember = useSelector((s) => s.authentication.teamMember);
  const business = useSelector((s) => s.authentication.chosenBusiness);
  const theme = useSelector(getCurrentTeamMemberTheme);
  const isIframe = useIframeCheck();
  const skipAccessFetch = useSkipAccessFetch();
  const [pageLoadTs] = useState(() => new Date().toISOString());
  const [pageLoadId] = useState(() => v4());
  const clientObjectId = business?.client_object?.id;

  // Whether a plugin blocks loading the entire app or not is determined by the plugin
  // base config value `block_loading_for_setup`. If one or more enabled plugins has this
  // set, we wait until the entire plugin is downloaded before showing any UI. This is
  // typically used for plugins that have conditional features like toolbar items or
  // data adornments, to avoid showing the UI with missing elements that load later.
  const blockingPluginApps = pluginApps?.filter(
    (pa) => pa.base_config?.block_loading_for_setup
  );

  const blockLoaderForPlugins = blockingPluginApps?.length > 0;

  useEffect(() => {
    if (!isIframe) {
      maybeIdentifyUser(teamMember?.user, business); // user <- auth user
    }
  }, [teamMember, isIframe, business]);

  useEffect(() => {
    if (theme) {
      applyTheme(theme);
      themeLocalStorage.val = theme;
    }
  }, [theme]);

  // Attempt to rehydrate authentication state
  // on first rendering of our root component
  useEffect(() => {
    if (isPseudo()) changeLanguageInStorage(PSEUDO_LANGUAGE);
    // When in an iframe, it is implied we are embedding a form and authentication is not required.
    // However, /embed pages are authorized iframes that do require rehydration.
    if (
      !isIframe ||
      window.location.pathname.startsWith(AUTHORIZED_EMBED_IFRAME_PATH)
    ) {
      rehydrate(skipAccessFetch);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const allRoutes = routes;
  const routeKeys = Object.keys(allRoutes);
  const filteredRouteKeys = routeKeys
    .filter((key) => !allRoutes[key].external)
    // Certain paths needs to be 'first' in the list to be matched
    .sort((a, b) => {
      if (priorityPaths.includes(a)) {
        return -1;
      }
      if (priorityPaths.includes(b)) {
        return 1;
      }
      return 0;
    });

  const routeComponents = filteredRouteKeys.map((routeKey, index) => {
    const route = allRoutes[routeKey];
    if (access) {
      // Some routes can have multiple paths, so we need to check the first one
      // for permissions in that case
      const [, mainPage, subPage] = (
        typeof route.path === 'string' ? route.path : route.path[0]
      ).split('/');

      // TODO need to create another solution how to handle permission by subpage
      const page = mainPage === 'commerce' ? subPage : mainPage;

      const accessSections = snakeToCamelCaseKeys(access.sections);

      let section;

      // need to check mutiple areas
      if (Array.isArray(urlsSections[page])) {
        section = urlsSections[page].reduce((acc, area) => {
          // any works as long as we have a true
          const hasAccess = (area.checkKey = ACCESS_ANY
            ? Object.values(accessSections[area.name]).includes(true)
            : accessSections[area.name]?.view);

          return acc || hasAccess ? { view: true } : undefined;
        }, undefined);
      } else {
        section = accessSections[urlsSections[page]];
      }

      if (section) {
        return (
          <RouteWithTitle
            key={index}
            path={route.path}
            exact={route.exact !== false}
            component={
              section.view
                ? route.component
                : () => {
                    throw new PermissionsError();
                  }
            }
            title={route.title}
          />
        );
      }
    }

    return (
      <RouteWithTitle
        key={index}
        path={route.path}
        exact={route.exact !== false}
        component={route.component}
        title={route.title}
      />
    );
  });

  const filterToolbar = useToolbarAccessFiltering();
  const loc = useLocation();
  const hideNav = shouldHideNavbarForPath(loc.pathname);
  const { t } = useTranslation();

  const appEnabled =
    Boolean(access) &&
    Boolean(teamMember?.id) &&
    Boolean(business?.id) &&
    business.has_active_subscription;

  const toolbarEnabled = appEnabled && !isIframe && !rehydrating && !hideNav;

  const { data: unfilteredToolbarData, isLoading: toolbarLoading } = useQuery({
    queryKey: PAGE_CONFIGS.MY_TOOLBAR({
      user: teamMember?.id,
      business: business?.id,
    }),
    queryFn: async () => {
      const res = await TeamMemberService.getPageConfig('toolbar');
      return {
        ...res,
        elements: res?.elements ?? [],
      };
    },
    refetchOnWindowFocus: false,
    staleTime: Infinity,
    enabled: toolbarEnabled,
  });

  const toolbarData = useMemo(() => {
    if (!toolbarEnabled || toolbarLoading) {
      return undefined;
    }

    return {
      ...unfilteredToolbarData,
      elements: filterToolbar(
        maybeInjectPermanentItems(
          unfilteredToolbarData?.elements ?? [],
          unfilteredToolbarData?.modified
        )
      ),
    };
  }, [filterToolbar, unfilteredToolbarData, toolbarEnabled, toolbarLoading]);

  const customLayoutKey = useMemo(
    () => serializeToKey(toolbarData?.elements ?? []),
    [toolbarData]
  );

  const {
    featureFlagsLoading,
    isLoading: pluginsLoading,
    toolbarItemsByFullId,
  } = useContext(PluginContext);

  const initialHydrationLoading = rehydrating && !isIframe;

  return (
    <>
      <ScrollbarInteractionManager />
      <MonitorDeploymentVersion history={history} />
      <Loader loading={initialHydrationLoading}>
        <BillingRouting enabled={appEnabled} />
        <InstrumentationProvider />
        <PageView
          pageLoadTs={pageLoadTs}
          pageLoadId={pageLoadId}
          enabled={appEnabled}
        />
        <DashboardAnalytics />
        {/* This provides translation context for KDS package*/}
        <TranslationContextProvider t={t}>
          <NavBarProvider>
            {/*
              We need this second loader here to ensure that we don't cause a
              re-render of the outer loading component when the toolbar loads
              for the first time
            */}
            <StyledLoader
              loading={
                toolbarLoading || (featureFlagsLoading && blockLoaderForPlugins)
              }
            >
              <AppBootstrapSuccess />
            </StyledLoader>
            <Spacing />
            <Typing />
            <ModalBackdrop />
            <WYSIWYGCopyPaste />

            {teamMember && business?.id && !isIframe ? (
              <MonitorSessionTimeout key={`session-monitor-${business?.id}`} />
            ) : null}

            <ErrorBoundaryResettable
              FallbackComponent={CatchError}
              sentryErrorBoundary
            >
              <AppContexts enabled={appEnabled}>
                <DataManagerSessionProvider>
                  <CustomNavigationWrapper>
                    {(expanded) => (
                      <CustomNavigation
                        layout={toolbarData?.elements ?? []}
                        expanded={expanded}
                        key={customLayoutKey}
                        loading={toolbarLoading}
                      />
                    )}
                  </CustomNavigationWrapper>
                  <ErrorBoundaryResettable
                    FallbackComponent={CatchError}
                    sentryErrorBoundary
                  >
                    {/* throw errors emerged from Axios */}
                    <AxiosErrorCatcher />
                    <Switch>
                      {/* Removes trailing slashes */}
                      <Redirect
                        from="/:url*(/+)"
                        to={`${window.location.pathname.replace(/\/+$/, '')}${
                          window.location.search
                        }`}
                      />
                      {/* Navigation to "/" should trigger a redirect to the first eligible item in the toolbar */}
                      <Route
                        path="/"
                        exact
                        component={() => {
                          // Need to wait for data to load before redirecting, when user has only one business.
                          // Plugin configs must be loaded too, otherwise plugin toolbar items are filtered out
                          // of the toolbar (and can't be resolved to a route), and we'd redirect to the wrong item
                          if (toolbarLoading || pluginsLoading) {
                            return <Loader loading />;
                          }

                          const firstNavItem = getFirstInternalNavItem(
                            toolbarData?.elements,
                            toolbarItemsByFullId
                          );
                          const redirectPath =
                            firstNavItem !== null &&
                            !isNonLandingPagePath(firstNavItem)
                              ? firstNavItem
                              : '/welcome';
                          return <Redirect to={redirectPath} />;
                        }}
                      />

                      {/* Redirect legacy /automations to /agentic-workflows, preserving search params */}
                      <Route
                        exact
                        path="/automations"
                        render={({ location }) => (
                          <Redirect
                            to={{
                              pathname: '/agentic-workflows',
                              search: location.search,
                            }}
                          />
                        )}
                      />

                      {/* Redirect legacy /automation/:id to /agentic-workflow/:id, preserving search params */}
                      <Route
                        exact
                        path="/automation/:id"
                        render={({ location, match }) => (
                          <Redirect
                            to={{
                              pathname: `/agentic-workflow/${match.params.id}`,
                              search: location.search,
                            }}
                          />
                        )}
                      />

                      {/* These redirects are to allow for old links using the client object ID instead of the name */}
                      {clientObjectId ? (
                        <Redirect
                          from={`/custom-objects/${clientObjectId}`}
                          to="/clients"
                          exact
                        />
                      ) : null}
                      {clientObjectId ? (
                        <Redirect
                          from={`/custom-objects/${clientObjectId}/:id/details`}
                          to={`${window.location.pathname.replace(
                            `/custom-objects/${clientObjectId}/`,
                            '/client/'
                          )}${window.location.search}`}
                          exact
                        />
                      ) : null}

                      {routeComponents}
                      <Route
                        key={filteredRouteKeys.length}
                        path="*"
                        component={NotFound}
                      />
                    </Switch>
                  </ErrorBoundaryResettable>
                </DataManagerSessionProvider>
              </AppContexts>
            </ErrorBoundaryResettable>
          </NavBarProvider>
        </TranslationContextProvider>
        <FloatingWindow />
        <RouteScriptHandler />
        <PaymentNeededHandler />
        <NotificationLinkHandler />
        <Suspense fallback={<Loader loading />}>
          <TestingMode />
        </Suspense>
      </Loader>
    </>
  );
};

const mapStateToProps = ({ authentication }) => ({
  rehydrating: authentication.rehydrating,
});

const mapDispatchToProps = (dispatch) => ({
  rehydrate: (skipAccessFetch = false) =>
    dispatch(serviceRehydrateStart(skipAccessFetch)),
});

export default connect(mapStateToProps, mapDispatchToProps)(App);
